import React, { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import ComponentList from "./ComponentList";
import PageEditHeader from "./PageEditHeader";
import PageTranslations from "./PageTranslations";
import PlatformTabs from "./PlatformTabs";
import ProjectAPI from "../../../../api/ProjectAPI";
import Button from "../../../../components/button/Button";
import "../../../../css/admin/utils/item-status.scss";
import { handleSlugErrors } from "../../../../utils/slug";

import type { AxiosError } from "axios";
import type { JSX } from "react";

import type {
  GetProjectPageComponentListResponse,
  GetProjectPageListResponse,
  GetProjectPageResponse,
  NewProjectPage,
  Platform,
  PlatformComponent,
  ProjectPage,
  ProjectPageTranslation,
} from "../../../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../../../components/routes/PlatformRoutes";
import type { PageFieldArray } from "../../../../contexts/FormTranslationsContext";
import type { AppState } from "../../../../reducers";

function isExistingProjectPage(
  page: ProjectPage | NewProjectPage,
): page is ProjectPage {
  return "uuid" in page;
}

function PageEdit(): JSX.Element {
  const history = useHistory();
  const { t } = useTranslation("editor-platform");
  const queryClient = useQueryClient();
  const {
    projectSlug,
    pageId,
    platform = "web",
  } = useParams<PlatformRoutesParams>();

  const projectBaseURL = useSelector(
    (state: AppState) => state.fetchReducer.projects.data,
  ).find((project) => project.name === projectSlug)?.base_url;

  const [platformState, setPlatformState] = useState<Platform>(platform);

  const methods = useForm<PageFieldArray<ProjectPageTranslation>>({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
  });

  const isNew = pageId === "new";

  const projectPageListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPageList,
    params: [projectSlug],
  });

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
  });

  const getProjectPageComponentListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPageComponentList,
    params: [{ pageId, platform, projectSlug }],
    options: {
      enabled: !isNew,
    },
  });

  const {
    data: projectPageComponentListData,
    isLoading: isLoadingComponents,
    isError: isComponentsError,
    refetch: refetchComponents,
  } = useQuery({
    ...getProjectPageComponentListQuery,
    select: (data) => data.components,
  });

  const [stateComponentList, setStateComponentList] = useState<
    PlatformComponent[]
  >([]);

  const deleteMutation = useMutation({
    mutationFn: async (componentId: string) =>
      ProjectAPI.deleteComponent({ componentId, projectSlug }),
    onMutate: async (componentId: string) => {
      await queryClient.cancelQueries(
        getProjectPageComponentListQuery.queryKey,
      );

      const previousComponentList =
        queryClient.getQueryData<GetProjectPageComponentListResponse>(
          getProjectPageComponentListQuery.queryKey,
        )?.components;

      if (previousComponentList) {
        queryClient.setQueryData(getProjectPageComponentListQuery.queryKey, {
          components: previousComponentList.filter(
            (component) => component.uuid !== componentId,
          ),
        });
      }

      return { previousComponentList };
    },
    onError: (_, __, context) => {
      if (context?.previousComponentList) {
        queryClient.setQueryData<GetProjectPageComponentListResponse>(
          getProjectPageComponentListQuery.queryKey,
          {
            components: context.previousComponentList,
          },
        );
      }
      toast.error(t("componentDeleteError"));
    },
    onSuccess: () => {
      toast.success(t("componentDeleteSuccess"));
    },
  });

  const handleDeleteComponent = (componentId: string): void => {
    deleteMutation.mutate(componentId);
  };

  const handlePlatformSwitch = (updatedPlatform: Platform): void => {
    history.replace(updatedPlatform);
  };

  useEffect(() => {
    if (
      stateComponentList.length !== projectPageComponentListData?.length ||
      platformState !== platform
    ) {
      const sortedComponentList =
        projectPageComponentListData?.sort((a, b) => a.rank - b.rank) ?? [];

      setStateComponentList(sortedComponentList);
      setPlatformState(platform);
    }
  }, [
    stateComponentList.length,
    projectPageComponentListData,
    platformState,
    platform,
  ]);

  const updateComponentMutation = useMutation({
    mutationKey: ["updateComponent"],
    mutationFn: async (updatedComponent: PlatformComponent) =>
      ProjectAPI.updateComponent({ component: updatedComponent, projectSlug }),
    onSuccess: () => {
      queryClient.invalidateQueries(getProjectPageComponentListQuery);
    },
  });

  const updatePageMutation = useMutation({
    mutationFn: async (updatedPage: ProjectPage | NewProjectPage) =>
      isExistingProjectPage(updatedPage)
        ? ProjectAPI.updateProjectPage({ pageId, projectSlug, updatedPage })
        : ProjectAPI.createProjectPage({ projectSlug, newPage: updatedPage }),
    onMutate: (updatedPage) => {
      if (isExistingProjectPage(updatedPage)) {
        const { label, enabled, uuid } = updatedPage;

        const previousPageData =
          queryClient.getQueryData<GetProjectPageResponse>(
            getProjectPageQuery.queryKey,
          );

        const previousPageListData =
          queryClient.getQueryData<GetProjectPageListResponse>(
            projectPageListQuery.queryKey,
          );

        if (previousPageListData) {
          queryClient.setQueryData(projectPageListQuery.queryKey, {
            ...previousPageListData,
            data: previousPageListData.data.map((page) =>
              page.uuid === uuid ? { ...page, label, enabled } : page,
            ),
          });
        }

        return { previousPageData, previousPageListData };
      }

      return {};
    },
    onSuccess: (response, updatedPage) => {
      if (!isExistingProjectPage(updatedPage)) {
        queryClient.invalidateQueries(getProjectPageQuery.queryKey);
        queryClient.invalidateQueries(projectPageListQuery.queryKey);

        const previousPathname = history.location.pathname;
        const newPathnameId = previousPathname.replace(
          /\/(new)/,
          `/${response.uuid}`,
        );

        history.replace(newPathnameId);
      } else {
        queryClient.setQueryData(getProjectPageQuery.queryKey, response);
      }
    },
    onError: (_, __, context) => {
      if (context?.previousPageData) {
        queryClient.setQueryData(
          getProjectPageQuery.queryKey,
          context.previousPageData,
        );
      }

      if (context?.previousPageListData) {
        queryClient.setQueryData(
          projectPageListQuery.queryKey,
          context.previousPageListData,
        );
      }
    },
  });

  const handleSave = async (
    updatedPage: ProjectPage | NewProjectPage,
  ): Promise<void> => {
    try {
      await methods.trigger();

      if (!methods.formState.isValid) return;

      const translations = methods.getValues("translations");

      const componentListToUpdate = isNew
        ? []
        : stateComponentList
            .filter(
              (component) =>
                component.last_rank != null &&
                component.last_rank !== component.rank,
            )
            .map(async (component) =>
              updateComponentMutation.mutateAsync(component),
            );

      await Promise.all([
        ...componentListToUpdate,
        updatePageMutation.mutateAsync({ ...updatedPage, translations }),
      ]);

      setStateComponentList((prevComponentList) =>
        prevComponentList.map((component) => ({
          ...component,
          last_rank: component.rank,
        })),
      );

      toast.success(t("saveSuccess"));
    } catch (error) {
      const slugErrorList = (
        error as AxiosError<{
          error: {
            translations: Record<string, string[]>[];
          };
        }>
      ).response?.data?.error?.translations;

      if (slugErrorList) {
        handleSlugErrors(slugErrorList);
      } else {
        toast.error(t("saveError"));
      }
    }
  };

  // L'état local est copié par un effet : tant qu'il n'est pas synchronisé, le tableau reste en squelette.
  const isSyncingComponents =
    projectPageComponentListData !== undefined &&
    (platformState !== platform ||
      (stateComponentList.length === 0 &&
        projectPageComponentListData.length > 0));

  const renderComponentSection = (): JSX.Element => (
    <ComponentList
      currentComponents={stateComponentList}
      isError={isComponentsError}
      isLoading={isLoadingComponents || isSyncingComponents}
      onDelete={handleDeleteComponent}
      onRetry={refetchComponents}
      onUpdate={setStateComponentList}
    />
  );

  const defaultPageTranslation = methods
    .getValues("translations")
    ?.find((translation) => translation.default);
  const externalPageUrl = `${projectBaseURL}/${defaultPageTranslation?.slug}`;

  return (
    <div className="flex size-full flex-col overflow-auto px-[3.75rem] py-[1.875rem]">
      <PageEditHeader onSave={handleSave} />
      <Button
        className="mb-[0.78125rem]"
        externalUrl={externalPageUrl}
        isDisabled={isNew || !projectBaseURL}
        size="sm"
        type="button"
        variant="secondary"
      />
      <PageTranslations methods={methods} />
      {!isNew && (
        <React.Fragment>
          <PlatformTabs
            selectedPlatform={platform}
            switchPlatform={handlePlatformSwitch}
          />
          {renderComponentSection()}
        </React.Fragment>
      )}
    </div>
  );
}

export default PageEdit;
